import { Context, Layer } from 'effect'
import type { PageStructuredText, ViewerPage } from './models'

type PresentedPageRenderState = {
  pageTextId: string | null
}

export type PageTextResolver = (pageTextId: string) => PageStructuredText | null

type PageTextAssetsService = {
  readonly store: (pageTextId: string, pageText: PageStructuredText) => void
  readonly remove: (pageTextId: string) => void
}

export class PageTextAssets extends Context.Service<PageTextAssets, PageTextAssetsService>()(
  'PageTextAssets'
) {}

/**
 * Store-scoped zero-copy handoff between the renderer and React. Managed code
 * owns writes through PageTextAssets; the view receives only the synchronous
 * resolver. The application scope clears the registry during teardown.
 */
export function makePageTextRegistry() {
  const pageTexts = new Map<string, PageStructuredText>()
  const service = {
    store: (pageTextId, pageText) => {
      pageTexts.set(pageTextId, pageText)
    },
    remove: (pageTextId) => {
      pageTexts.delete(pageTextId)
    }
  } satisfies PageTextAssetsService

  return {
    resolve: ((pageTextId) => pageTexts.get(pageTextId) ?? null) satisfies PageTextResolver,
    layer: Layer.succeed(PageTextAssets, service),
    clear: () => pageTexts.clear()
  }
}

export function resolvePresentedPageStructure<
  P extends ViewerPage & { textRects: PageStructuredText['textRects'] }
>(
  pageStructure: P,
  pageRenderState: PresentedPageRenderState | null | undefined,
  resolvePageText: PageTextResolver
): P {
  const pageTextId = pageRenderState?.pageTextId
  if (!pageTextId) {
    return pageStructure
  }

  const pageText = resolvePageText(pageTextId)
  if (!pageText) {
    return pageStructure
  }

  return {
    ...pageStructure,
    textRects: pageText.textRects
  }
}
